import {Directive, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {asyncScheduler, Observable, Subject} from 'rxjs';
import {buffer, debounceTime, distinctUntilChanged, filter, map, takeUntil, throttleTime} from 'rxjs/operators';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {BarcodeScannerService} from '../../barcode-scanner/barcode-scanner.service';
import {
  ScanBarcodeWithCameraDialog
} from '../../barcode-scanner/scan-barcode-with-camera-dialog/scan-barcode-with-camera-dialog.component';
import {SortingService} from '../../_services/sorting.service';
import {Stock} from '../stock';
import {AbstractBaseCountComponent} from './abstract-base-count.component';

export class BedWithVoorraad<T extends Stock = Stock> {
  naam: string;
  voorraad: T[];
  loading = false;

  constructor(naam: string, voorraad?: T[]) {
    this.naam = naam;
    this.voorraad = voorraad;
  }
}

@Directive()
export abstract class AbstractCountComponent<TStock extends Stock = Stock>
  extends AbstractBaseCountComponent<TStock>
  implements OnInit {

  allBeds: BedWithVoorraad<TStock>[] = [];
  visibleBeds: BedWithVoorraad<TStock>[] = [];
  displayedColumns = ['naam', 'total', 'vrij', 'vr', 'potmaat'];
  barcodeInputSubject = new Subject<number>();
  barcodeInput$ = this.barcodeInputSubject.asObservable();
  selectedPosition: string;

  protected lastSelectedBed: BedWithVoorraad<TStock>;

  protected abstract readonly selectedPositionKey: string;

  constructor(
    protected route: ActivatedRoute,
    protected dialog: MatDialog,
    protected barcodeScannerService: BarcodeScannerService,
    sortingService: SortingService
  ) {
    super(sortingService);
  }

  protected abstract fetchBedNrs(): Observable<string[]>;
  protected abstract fetchStockByBedNr(bedNaam: string): Observable<TStock[]>;
  protected abstract updateStockItem(stock: TStock): Observable<any>;
  abstract openAddStockDialog(): void;

  ngOnInit() {
    this.selectedPosition = localStorage.getItem(this.selectedPositionKey) || '';
    this.setupSearchFieldBehaviour();
    this.fetchAllBeds().then(() => {
      this.searchTerm = this.route.snapshot.paramMap.get('bednr') || this.route.snapshot.paramMap.get('location');
      this.searchTermSubject.next(this.searchTerm);
    });
    this.setupBarcodeScanning();
  }

  async fetchAllBeds() {
    this.fetchingBedsSubject.next(true);
    try {
      const beds = await this.fetchBedNrs().toPromise();
      this.allBeds = beds
        .filter(val => val !== null)
        .sort((a, b) => {
          return this.useCustomSort
            ? this.sortingService.customLocationCompare(a, b)
            : a.localeCompare(b);
        })
        .map(bed => new BedWithVoorraad<TStock>(bed));
    } catch (error) {
      console.error('Unable to get all beds: ' + error);
    }
    this.fetchingBedsSubject.next(false);
  }

  syncVisibleBedsWithAllBeds() {
    this.visibleBeds = this.allBeds.slice(this.firstVisibleBedIndex, this.firstVisibleBedIndex + this.AMOUNT_BEDS_VISIBLE);
  }

  async fetchVoorraad(bed: BedWithVoorraad<TStock>) {
    bed.loading = true;
    try {
      const retrievedStock = await this.fetchStockByBedNr(bed.naam).toPromise();
      bed.voorraad = (retrievedStock || []).sort((a, b) => (a.plantName || '').localeCompare(b.plantName || ''));
      bed.loading = false;
    } catch (error) {
      console.error('Unable to get voorraad for bednr ' + bed.naam + '\n' + error);
    }
  }

  getFilteredBeds(term?: string): BedWithVoorraad<TStock>[] {
    const normalizedTerm = term ? term.trim().toLowerCase() : '';
    const normalizedPosition = this.selectedPosition ? this.selectedPosition.trim().toLowerCase() : '';

    return this.allBeds.filter(bed => {
      const bedNaam = bed.naam ? bed.naam.toLowerCase().trim() : '';
      const matchesTerm = normalizedTerm ? bedNaam.startsWith(normalizedTerm) : true;
      const matchesPosition = normalizedPosition ? bedNaam.endsWith(normalizedPosition) : true;
      return matchesTerm && matchesPosition;
    });
  }

  loadMoreItemsBottom() {
    const beds = this.getBedsForSelectedPosition();
    const lastVisibleBed = this.visibleBeds[this.visibleBeds.length - 1];
    const lastIndex = lastVisibleBed ? beds.indexOf(lastVisibleBed) : -1;
    const bedsToAdd: BedWithVoorraad<TStock>[] = lastIndex !== -1
      ? beds.slice(lastIndex + 1, lastIndex + 6)
      : [];
    bedsToAdd.forEach(bed => this.visibleBeds.push(bed));
    const amountOfBedsToHide = Math.max(this.visibleBeds.length - this.AMOUNT_BEDS_VISIBLE, 0);
    this.visibleBeds.splice(0, amountOfBedsToHide);
    this.firstVisibleBedIndex = this.visibleBeds.length > 0 ? this.allBeds.indexOf(this.visibleBeds[0]) : 0;
    bedsToAdd.forEach(bed => this.fetchVoorraad(bed));
  }

  loadMoreItemsTop() {
    const beds = this.getBedsForSelectedPosition();
    const firstVisibleBed = this.visibleBeds[0];
    const firstIndex = firstVisibleBed ? beds.indexOf(firstVisibleBed) : -1;
    const bedsToAdd: BedWithVoorraad<TStock>[] = firstIndex > 0
      ? beds.slice(Math.max(0, firstIndex - 5), firstIndex)
      : [];
    bedsToAdd.reverse().forEach(bed => this.visibleBeds.unshift(bed));
    const amountOfBedsToHide = Math.max(this.visibleBeds.length - this.AMOUNT_BEDS_VISIBLE, 0);
    this.visibleBeds.splice(-amountOfBedsToHide, amountOfBedsToHide);
    this.firstVisibleBedIndex = this.visibleBeds.length > 0 ? this.allBeds.indexOf(this.visibleBeds[0]) : 0;
    bedsToAdd.forEach(bed => this.fetchVoorraad(bed));
  }

  private getBedsForSelectedPosition() {
    return this.selectedPosition
      ? this.allBeds.filter(bed => bed.naam?.toLowerCase().trim().endsWith(this.selectedPosition.toLowerCase()))
      : this.allBeds;
  }

  setLastSelectedBed(bed: BedWithVoorraad<TStock>): void {
    this.lastSelectedBed = bed;
  }

  public async onAttach() {
    if (this.lastSelectedBed) {
      this.fetchVoorraad(this.lastSelectedBed);
    }
    await super.onAttach();
  }

  private setupSearchFieldBehaviour() {
    this.searchTermSubject.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(1000),
      filter(str => str && str.length >= 2),
      distinctUntilChanged(),
    ).subscribe(searchTerm => this.applyFilter(searchTerm));
  }

  private applyFilter(term: string) {
    this.visibleBeds = [];

    if (term && term.length >= 2) {
      const filteredBeds = this.getFilteredBeds(term);

      if (filteredBeds.length > 0) {
        this.noBedsFoundMessage = undefined;
        this.firstVisibleBedIndex = this.allBeds.indexOf(filteredBeds[0]);
        this.visibleBeds = filteredBeds;
        this.visibleBeds.forEach(bed => this.fetchVoorraad(bed));
      } else {
        this.noBedsFoundMessage = 'Geen bedden gevonden voor zoekterm: ' + term;
      }
    } else {
      this.noBedsFoundMessage = undefined;
      this.visibleBeds = [];
    }
  }

  async setLastInspection(event: MouseEvent, stock: TStock, bed: BedWithVoorraad<TStock>) {
    event.stopPropagation();
    await this.updateStockInspection(stock);
    await this.fetchVoorraad(bed);
  }

  async setLastInspectionForLocation(event: MouseEvent, bed: BedWithVoorraad<TStock>) {
    event.stopPropagation();
    const updatePromises = (bed.voorraad || []).map((stock: TStock) => this.updateStockInspection(stock));
    await Promise.all(updatePromises);
    await this.fetchVoorraad(bed);
  }

  private async updateStockInspection(stock: TStock) {
    this.applyInspectionToggle(stock);
    await this.updateStockItem(stock).toPromise();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.defaultPrevented || (event.target instanceof HTMLInputElement)) {
      return;
    }
    const number = Number(event.key);
    if (number >= 0) {
      this.barcodeInputSubject.next(number);
      event.preventDefault();
    }
  }

  private setupBarcodeScanning() {
    this.subscriptions.add(this.barcodeInput$.pipe(
      buffer(this.barcodeInput$.pipe(throttleTime(100, asyncScheduler, {leading: false, trailing: true}))),
      filter(barcodeArray => barcodeArray.length > 3),
      map(barcodeArray => +barcodeArray.join(''))
    ).subscribe({
      next: (barcode: number) => {
        this.barcodeScannerService.nextBarcode(barcode);
      }
    }));
  }

  openCameraScanner() {
    const dialog: MatDialogRef<ScanBarcodeWithCameraDialog> = this.dialog.open(ScanBarcodeWithCameraDialog, {});
    dialog.afterClosed().subscribe((barcode: number) => {
      if (barcode) {
        this.barcodeScannerService.nextBarcode(barcode);
      }
    });
  }

  onSelectedPositionChange() {
    localStorage.setItem(this.selectedPositionKey, this.selectedPosition || '');
    this.reapplyFilter();
  }

  onSortToggleChange(checked: boolean) {
    this.useCustomSort = checked;
    this.applySorting();
  }

  private applySorting() {
    if (!this.allBeds) {
      return;
    }

    this.allBeds.sort((a, b) => {
      return this.useCustomSort
        ? this.sortingService.customLocationCompare(a.naam, b.naam)
        : a.naam.localeCompare(b.naam);
    });

    this.syncVisibleBedsWithAllBeds();
    this.reapplyFilter();
  }

  protected reapplyFilter() {
    if (this.searchTermSubject.value) {
      this.applyFilter(this.searchTermSubject.value);
    }
  }
}
